/**
 * Testimonials Section
 * Customer reviews and testimonials
 */

import React, { useState, useEffect, useCallback } from 'react';
import useDarkModeNeutrals from '../../hooks/useDarkModeNeutrals';
import './sections.css';

function Testimonials({ settings = {}, content = {}, globalStyles = {}, store }) {
  const neutrals = useDarkModeNeutrals();
  const {
    layout = 'carousel', // carousel, grid
    showPhotos = true,
    showRating = true,
    maxItems = 6,
    // Granular style settings — neutrals flip in dark mode, brand
    // values (starColor) stay.
    backgroundColor = neutrals.surfaceAlt,
    titleColor = neutrals.text,
    cardBgColor = neutrals.surface,
    quoteColor = neutrals.text,
    authorColor = neutrals.text,
    starColor = '#fbbf24',
    cardRadius = '12px',
  } = settings;

  const {
    title = 'What Our Customers Say',
    subtitle = '',
    testimonials = [], // Manual testimonials or will fetch from store reviews
  } = content;

  const [reviews, setReviews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const colors = globalStyles.colors || {};
  const fonts = globalStyles.fonts || {};

  useEffect(() => {
    const fetchReviews = async () => {
      // Use manual testimonials if provided
      if (testimonials.length > 0) {
        setReviews(testimonials.slice(0, maxItems));
        setLoading(false);
        return;
      }

      if (!store?.slug) {
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`/api/store-reviews/store/${store.slug}`);
        const data = await res.json();

        if (data.success && data.reviews) {
          // Only show active reviews, limited by maxItems
          setReviews(data.reviews.slice(0, maxItems));
        }
      } catch (err) {
        console.error('Failed to fetch reviews:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [store, testimonials, maxItems]);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
  }, [reviews.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  }, [reviews.length]);

  const sectionStyle = {
    backgroundColor,
  };

  const titleStyle = {
    fontFamily: fonts.heading || 'inherit',
    color: titleColor,
  };

  const cardStyle = {
    backgroundColor: cardBgColor,
    borderRadius: cardRadius,
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <svg
        key={i}
        width="16"
        height="16"
        fill={i < rating ? starColor : 'none'}
        stroke={starColor}
        strokeWidth="2"
        viewBox="0 0 24 24"
      >
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ));
  };

  if (loading) {
    return (
      <section className="ts-testimonials">
        <div className="ts-testimonials__loading">
          <div className="ts-testimonials__spinner" />
        </div>
      </section>
    );
  }

  if (reviews.length === 0) {
    return null; // Don't render if no reviews
  }

  return (
    <section className="ts-testimonials" style={sectionStyle}>
      <div className="ts-testimonials__container">
        {title && (
          <div className="ts-testimonials__header">
            <h2 className="ts-testimonials__title" style={titleStyle}>
              {title}
            </h2>
            {subtitle && (
              <p className="ts-testimonials__subtitle">{subtitle}</p>
            )}
          </div>
        )}

        {layout === 'carousel' ? (
          <div className="ts-testimonials__carousel">
            <div className="ts-testimonials__track">
              {reviews.map((review, index) => (
                <div
                  key={review.id || index}
                  className={`ts-testimonial-card ${index === currentIndex ? 'ts-testimonial-card--active' : ''}`}
                  style={cardStyle}
                >
                  {showRating && review.rating && (
                    <div className="ts-testimonial-card__rating">
                      {renderStars(review.rating)}
                    </div>
                  )}
                  <blockquote className="ts-testimonial-card__quote" style={{ color: quoteColor }}>
                    "{review.reviewText || review.content || review.text}"
                  </blockquote>
                  <div className="ts-testimonial-card__author">
                    {showPhotos && (review.customerImage || review.photo) && (
                      <img
                        src={review.customerImage || review.photo}
                        alt={review.customerName || review.name || review.authorName}
                        className="ts-testimonial-card__avatar"
                      />
                    )}
                    <div className="ts-testimonial-card__author-info">
                      <span className="ts-testimonial-card__name" style={{ color: authorColor }}>
                        {review.customerName || review.name || review.authorName}
                      </span>
                      {review.title && (
                        <span className="ts-testimonial-card__author-title">
                          {review.title}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {reviews.length > 1 && (
              <>
                <button
                  className="ts-testimonials__arrow ts-testimonials__arrow--prev"
                  onClick={prevSlide}
                  aria-label="Previous testimonial"
                >
                  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <button
                  className="ts-testimonials__arrow ts-testimonials__arrow--next"
                  onClick={nextSlide}
                  aria-label="Next testimonial"
                >
                  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
                <div className="ts-testimonials__dots">
                  {reviews.map((_, index) => (
                    <button
                      key={index}
                      className={`ts-testimonials__dot ${index === currentIndex ? 'ts-testimonials__dot--active' : ''}`}
                      onClick={() => setCurrentIndex(index)}
                      aria-label={`Go to testimonial ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="ts-testimonials__grid">
            {reviews.map((review, index) => (
              <div key={review.id || index} className="ts-testimonial-card" style={cardStyle}>
                {showRating && review.rating && (
                  <div className="ts-testimonial-card__rating">
                    {renderStars(review.rating)}
                  </div>
                )}
                <blockquote className="ts-testimonial-card__quote" style={{ color: quoteColor }}>
                  "{review.reviewText || review.content || review.text}"
                </blockquote>
                <div className="ts-testimonial-card__author">
                  {showPhotos && (review.customerImage || review.photo) && (
                    <img
                      src={review.customerImage || review.photo}
                      alt={review.customerName || review.name || review.authorName}
                      className="ts-testimonial-card__avatar"
                    />
                  )}
                  <div className="ts-testimonial-card__author-info">
                    <span className="ts-testimonial-card__name" style={{ color: authorColor }}>
                      {review.customerName || review.name || review.authorName}
                    </span>
                    {review.title && (
                      <span className="ts-testimonial-card__author-title">
                        {review.title}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default React.memo(Testimonials);
